import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import WaitingRoom from "@/pages/WaitingRoom";
import { setSessionActive } from "@/lib/sessionHost";

/**
 * Iteration 86 — GLOBAL PLAYBACK.
 *
 * Playback belongs to the application, not to a page. SessionHost keeps the
 * active <WaitingRoom /> mounted OUTSIDE the route tree: navigating anywhere
 * inside Continua merely hides the session view (display:none) while every
 * audio element, WebAudio mixer, timer and piece of state keeps running.
 * Returning to /waiting simply reveals the exact same component — scene,
 * controls, progress and presence indicator — as if the user never left.
 *
 * Lifecycle:
 *  • A NEW session starts when /waiting is opened with a fresh `launchId`
 *    in the navigation state (the Home "Play" button mints one). Remounting
 *    with a new key tears down the old session's audio first.
 *  • Re-opening /waiting without a launchId (or with the same one)
 *    REATTACHES to the running session.
 *  • WaitingRoom dispatches "continua:session-closed" whenever it exits
 *    (leave button, in-universe error, mount guard) → host unmounts it.
 */
export const SessionHost = () => {
  const location = useLocation();
  const [active, setActive] = useState(null);
  const isWaiting = location.pathname === "/waiting";

  useEffect(() => {
    if (!isWaiting) return;
    const s = location.state || null;
    if (!s) return; // reattach view only — never spawn a session without params
    setActive((prev) => {
      if (prev && (!s.launchId || s.launchId === prev.launchId)) return prev; // reattach
      return { key: s.launchId || Date.now(), launchId: s.launchId || null, params: s };
    });
  }, [isWaiting, location.state]);

  useEffect(() => {
    const done = () => setActive(null);
    window.addEventListener("continua:session-closed", done);
    return () => window.removeEventListener("continua:session-closed", done);
  }, []);

  useEffect(() => {
    setSessionActive(!!active);
    return () => setSessionActive(false);
  }, [active]);

  // Deep link to /waiting with no state and no running session → old behavior.
  if (isWaiting && !active && !location.state) return <Navigate to="/app" replace />;
  if (!active) return null;
  return (
    <div
      style={{ display: isWaiting ? "block" : "none" }}
      aria-hidden={!isWaiting}
      inert={!isWaiting || undefined}
      data-testid="session-host"
    >
      <WaitingRoom key={active.key} hostParams={active.params} />
    </div>
  );
};
